const TOKEN_KEY = 'token';
const LEGACY_TOKEN_KEYS = ['access_token', 'accessToken', 'adminToken'];
const USER_KEY = 'user';
const EXPIRY_SKEW_SECONDS = 5;

function safeStorage() {
  try {
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

export function getAdminToken() {
  const storage = safeStorage();
  if (!storage) return '';

  // 새 로그인에서 저장하는 canonical token을 항상 최우선으로 사용한다.
  const canonical = storage.getItem(TOKEN_KEY);
  if (canonical) return canonical;

  // 구버전 브라우저 저장값 호환용 fallback.
  for (const key of LEGACY_TOKEN_KEYS) {
    const token = storage.getItem(key);
    if (token) return token;
  }

  return '';
}

export function getStoredAdminUser() {
  const storage = safeStorage();
  if (!storage) return {};
  try {
    return JSON.parse(storage.getItem(USER_KEY) || '{}');
  } catch (_) {
    return {};
  }
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const json = window.atob(padded);
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

export function isAdminTokenValid(token = getAdminToken()) {
  const payload = decodeJwtPayload(token);
  if (!payload || payload.type !== 'admin') return false;

  const exp = Number(payload.exp);
  if (!Number.isFinite(exp) || exp <= 0) return false;

  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp > nowSeconds + EXPIRY_SKEW_SECONDS;
}

export function clearAdminSession() {
  const storage = safeStorage();
  if (!storage) return;

  storage.removeItem(TOKEN_KEY);
  LEGACY_TOKEN_KEYS.forEach((key) => storage.removeItem(key));
  storage.removeItem(USER_KEY);
}

export function storeAdminSession(token, user) {
  const storage = safeStorage();
  if (!storage) return;

  // 만료된 구버전 토큰이 새 토큰보다 우선 사용되는 문제를 원천 차단한다.
  LEGACY_TOKEN_KEYS.forEach((key) => storage.removeItem(key));
  storage.setItem(TOKEN_KEY, String(token || ''));
  storage.setItem(USER_KEY, JSON.stringify(user || {}));
}

function isAuthPage(pathname) {
  return pathname === '/login' || pathname === '/signup';
}

export function expireAdminSession({ redirect = true } = {}) {
  clearAdminSession();

  if (!redirect || typeof window === 'undefined') return;
  if (isAuthPage(window.location.pathname)) return;

  // replace를 사용해 만료된 보호 페이지로 뒤로가기가 반복되는 현상을 방지한다.
  window.location.replace('/login');
}

export function getAdminAuthHeaders() {
  const token = getAdminToken();
  if (!token) return {};

  if (!isAdminTokenValid(token)) {
    expireAdminSession();
    return {};
  }

  return { Authorization: `Bearer ${token}` };
}

export function isPublicAdminAuthRequest(url = '') {
  const value = String(url || '');
  return /\/admin\/auth\/(?:login|register|signup)(?:[/?#]|$)/i.test(value);
}

export function handleAdminUnauthorized(status, requestUrl = '', responseData = null) {
  if (Number(status) !== 401) return false;
  if (isPublicAdminAuthRequest(requestUrl)) return false;

  // Defensive compatibility for a backend that still forwards cashier HTTP 401:
  // it is not a failure of this browser's admin JWT. Genuine admin-auth 401s
  // (including /admin/payment routes) still expire the session normally.
  const code = String(responseData?.cashier?.code || '');
  if (/\/admin\/payment(?:[/?#]|$)/i.test(String(requestUrl || '')) &&
      /^internal_api_(?:unauthorized|bad_signature|signature_required|timestamp_expired|credentials_must_be_distinct)$/.test(code)) {
    return false;
  }

  expireAdminSession();
  return true;
}
